/**
 * 工作台首页：机构信息 + KPI。
 *
 * 两条口径：
 *   ① **数字全部来自接口**（`/api/provider/overview`），界面不自行累加、不四舍五入；
 *   ② **缺失显示「未提供」，绝不用 0 冒充** —— 服务方按 KPI 安排人力与车辆，
 *      一个凭空的下班 0 会被读成「今天没有单」。
 *      （本页里 `0` 只可能来自接口真的返回了 0。）
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { providerApi } from '../lib/api.ts';
import type { OverviewResult, ProviderSession } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { admissionStatusText, bookingStatusText, formatDateTime, formatNumber, formatText, subjectTypeText } from '../lib/format.ts';
import { Badge, Card, Kpi, PageHead } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';

export function OverviewPage({ session }: { session: ProviderSession }) {
  const overview = useLoad<OverviewResult>((signal) => providerApi.overview(signal));

  const kpi = overview.data?.kpi;
  const byStatus = overview.data?.byStatus;

  const kpiCards: { label: string; value: ReactNode; tone?: 'danger' | 'warn' | 'e' | 'w'; delta?: ReactNode }[] = [
    {
      label: '待接单',
      value: formatNumber(kpi?.awaitingAccept),
      tone: 'warn',
      delta: '待受理 + 已派单 + 已预约（尚未被本机构承接或正在做的单）',
    },
    { label: '进行中', value: formatNumber(kpi?.inProgress), tone: 'e', delta: '状态为「服务中」的工单' },
    { label: '今日', value: formatNumber(kpi?.today), delta: '按号源开始时间落在今天（服务时间口径，不是下单时间）' },
    { label: '已完成', value: formatNumber(kpi?.completed), delta: '状态为「已完成」的工单' },
    { label: '未到场', value: formatNumber(kpi?.noShow), tone: 'danger', delta: '成员未到场（独立终态，不退号）' },
    { label: '已取消', value: formatNumber(kpi?.cancelled), delta: '已取消（含服务方取消与成员侧取消）' },
  ];

  return (
    <>
      <PageHead
        title="工作台概览"
        sub="OVERVIEW · 本机构名下的工单计数与绑定主体状态"
        actions={
          <Link className="btn sm" to="/bookings">
            进入工单列表
          </Link>
        }
      />

      <div className="grid3" style={{ marginBottom: 14 }}>
        <Kpi
          label="机构名称"
          value={<span style={{ fontSize: 18 }}>{formatText(session.provider.name)}</span>}
          delta={`联系人 ${formatText(session.provider.contactName)} · ${formatText(session.provider.phone)}`}
        />
        <Kpi
          label="绑定主体准入状态"
          value={
            <span style={{ fontSize: 18 }}>{admissionStatusText(session.subject.admissionStatus)}</span>
          }
          tone={session.subject.bookable ? undefined : 'danger'}
          delta={`${formatText(session.subject.name)} · ${subjectTypeText(session.subject.subjectType)} · ${session.subject.city || '城市未提供'}`}
        />
        <Kpi
          label="可约资源数"
          value={formatNumber(session.resources.bookable)}
          delta={`该主体名下服务资源共 ${formatNumber(session.resources.total)} 项；「可约」与成员端看到的口径同源`}
        />
      </div>

      <Card
        title="工单 KPI"
        sub="PROVIDER KPI"
        actions={
          <button type="button" className="btn sm" onClick={overview.reload} disabled={overview.loading}>
            刷新
          </button>
        }
      >
        {overview.loading && <LoadingBox text="正在读取工单计数…" />}
        {!overview.loading && overview.error !== null && (
          <ErrorBox message={errorText(overview.error)} onRetry={overview.reload} />
        )}
        {!overview.loading && overview.error === null && kpi !== undefined && (
          <>
            <div className="grid3">
              {kpiCards.map((item) => (
                <Kpi key={item.label} label={item.label} value={item.value} tone={item.tone} delta={item.delta} />
              ))}
            </div>
            <div className="divider" />
            <div className="text-tiny muted">按状态明细（原始状态键 → 中文名取自平台共享口径）：</div>
            <div className="chip-row" style={{ marginTop: 8 }}>
              {Object.entries(byStatus ?? {}).map(([status, count]) => (
                <span key={status} className="badge ghost">
                  {bookingStatusText(status)} <span className="mono-num">{formatNumber(count)}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card title="授权来源与账号状态" sub="IDENTITY BOUNDARY">
        <div className="kv">
          <div>
            <div className="kv-l">账号手机号</div>
            <div className="kv-v">{formatText(session.provider.phone)}</div>
          </div>
          <div>
            <div className="kv-l">上次登录</div>
            <div className="kv-v">{formatDateTime(session.provider.lastLoginAt)}</div>
          </div>
          <div>
            <div className="kv-l">二次验证（TOTP）</div>
            <div className="kv-v">
              {session.provider.totpEnabled ? (
                <Badge tone="ok">已启用</Badge>
              ) : (
                <Badge tone="warn">未启用（本期未开放，表结构已预留）</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="perm-block" style={{ marginTop: 14 }}>
          <div className="pb-t">🔒 {session.authorization}</div>
          本工作台<b>不提供</b>按成员检索，也<b>不提供</b>任何导出入口 ——
          接口层面不存在这两个能力，界面因此也没有对应的入口。
          每次读取成员信息（列表、详情、总览）平台都会写一条审计记录，成员可以问「谁在什么时候看了我的资料」。
        </div>
      </Card>
    </>
  );
}
